import { Link } from "react-router-dom";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SectionHeading from "@/components/feature/SectionHeading";
import { siteContact, testimonials, siteStats } from "@/mocks/clueData";
import { useSeo } from "@/hooks/useSeo";

const principles = [
  {
    icon: "ri-focus-3-line",
    title: "先核实，再发布",
    desc: "涉及具体时间、地点与第三方陈述的内容，我们会先与投稿人沟通补充材料，无法核实的部分宁可暂缓发布。",
  },
  {
    icon: "ri-lock-2-line",
    title: "隐私优先",
    desc: "匿名投稿不展示个人信息，实名投稿也会先征得同意。涉及他人隐私的细节，编辑会在整理时主动隐去。",
  },
  {
    icon: "ri-scales-3-line",
    title: "克制表达",
    desc: "我们避免情绪化标题与单方面定性，尽量把事实、投稿人观点与平台说明分开呈现，交给你自己判断。",
  },
  {
    icon: "ri-refresh-line",
    title: "允许更正",
    desc: "内容上线后如出现新的进展或事实修正，我们会在原页面追加说明，并保留修改记录，方便回溯。",
  },
];

export default function AboutPage() {
  useSeo({
    title: "关于我们 - 17.cc网友爆料站的定位与内容原则",
    description:
      "了解17.cc网友爆料站的品牌定位、发展理念与内容原则。我们是一个面向网友的爆料与投稿社区，坚持先核实再发布、隐私优先与克制表达，并提供线索征集与投稿渠道。",
    keywords: "17.cc网友爆料,关于我们,内容原则,17.cc用户投稿,线索征集",
    path: "/about",
    schema: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于我们 - 17.cc 网友爆料站",
        description: "17.cc 网友爆料站的品牌定位、发展理念与内容原则。",
        url: "https://17.cc/about",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://17.cc/" },
          { "@type": "ListItem", position: 2, name: "关于我们", item: "https://17.cc/about" },
        ],
      },
    ],
  });

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-12">
      <Breadcrumbs items={[{ label: "关于我们" }]} />

      <section className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
        <div className="lg:col-span-7">
          <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground-950 md:text-4xl">
            关于 17.cc 网友爆料站
          </h1>
          <p className="mt-5 text-sm leading-8 text-foreground-700 md:text-base">
            {siteContact.brandCn} 是一个面向普通网友的爆料与投稿社区。我们相信，
            很多值得被看见的事，最初只是某个人身边的一段经历。这里想做的，是给这些经历一个
            被认真对待、被清晰讲述的地方。
          </p>
          <p className="mt-4 text-sm leading-8 text-foreground-700 md:text-base">
            我们不做情绪的放大镜，也不想成为未核实信息的传声筒。相比“抢首发”，
            我们更在意内容是否经得起阅读——时间线是否清楚、信息来源是否可追溯、
            涉及他人的部分是否已做保护。
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/submit"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-send-plane-line text-base"></i>
              投稿你的故事
            </Link>
            <Link
              to="/clues"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100"
            >
              浏览热门爆料
            </Link>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
            <h2 className="font-heading text-base font-semibold text-foreground-950">网站定位</h2>
            <p className="mt-3 text-sm leading-7 text-foreground-600">
              我们更接近一个“线索整理站”，而不是新闻门户。网友投稿是内容的主体，
              编辑团队负责核实、梳理与排版，让零散的信息变成可以读完的段落。
            </p>
            <dl className="mt-6 grid grid-cols-2 gap-4">
              {siteStats.map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-md border border-background-200 bg-background-50 px-4 py-3"
                >
                  <dd className="font-heading text-lg font-bold text-primary-700">{stat.value}</dd>
                  <dt className="mt-1 text-xs text-foreground-500">{stat.label}</dt>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      <section className="mt-16 border-t border-background-200 pt-12">
        <SectionHeading
          eyebrow="内容原则"
          icon="ri-compasses-2-line"
          title="我们坚持的四条底线"
          desc="这些原则决定了什么内容会被发布，也决定了我们如何面对争议与更正请求。"
        />
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
          {principles.map((item) => (
            <article
              key={item.title}
              className="rounded-lg border border-background-200 bg-background-50 p-6"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                <i className={`${item.icon} text-xl`}></i>
              </span>
              <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                {item.title}
              </h3>
              <p className="mt-2 text-sm leading-7 text-foreground-600">{item.desc}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="mt-16 border-t border-background-200 pt-12">
        <SectionHeading eyebrow="网站口碑" icon="ri-chat-quote-line" title="来自网友的声音" />
        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
          {testimonials.map((item) => (
            <figure
              key={item.name}
              className="flex h-full flex-col rounded-lg border border-background-200 bg-background-100 p-6"
            >
              <i className="ri-double-quotes-l text-2xl text-accent-500"></i>
              <blockquote className="mt-3 flex-1 text-sm leading-7 text-foreground-700">
                {item.text}
              </blockquote>
              <figcaption className="mt-5 border-t border-background-300/60 pt-4">
                <span className="block font-heading text-sm font-semibold text-foreground-950">
                  {item.name}
                </span>
                <span className="mt-0.5 block text-xs text-foreground-500">{item.role}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="mt-16 grid grid-cols-1 gap-6 rounded-lg border border-secondary-200 bg-secondary-50 p-6 md:grid-cols-3 md:p-8">
        <div className="md:col-span-1">
          <h2 className="font-heading text-xl font-bold text-foreground-950">联系我们</h2>
          <p className="mt-3 text-sm leading-7 text-foreground-600">
            内容更正、隐私处理与合作咨询，都可以通过下面的邮箱联系。我们会在工作日内回复。
          </p>
        </div>
        <div className="flex flex-col gap-4 md:col-span-2 md:flex-row">
          <div className="flex flex-1 items-start gap-3 rounded-md border border-background-200 bg-background-50 p-5">
            <i className="ri-mail-send-line mt-0.5 text-xl text-primary-600"></i>
            <div>
              <h3 className="text-sm font-semibold text-foreground-950">一般咨询与更正</h3>
              <p className="mt-1 text-sm text-foreground-600">{siteContact.email}</p>
            </div>
          </div>
          <div className="flex flex-1 items-start gap-3 rounded-md border border-background-200 bg-background-50 p-5">
            <i className="ri-lightbulb-line mt-0.5 text-xl text-accent-600"></i>
            <div>
              <h3 className="text-sm font-semibold text-foreground-950">线索征集与合作</h3>
              <p className="mt-1 text-sm text-foreground-600">{siteContact.cooperateEmail}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}